/**
 * ReportExportButton - Export ontology analysis report
 *
 * Dropdown button that allows users to download ontology analysis
 * reports in HTML or PDF format.
 *
 * @version 1.0.0
 * @date 2026-02-07
 */

import React, { useState, useRef, useEffect } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import './ReportExportButton.css';

interface ReportExportButtonProps {
  ontologyId: string;
  disabled?: boolean;
}

export const ReportExportButton: React.FC<ReportExportButtonProps> = ({
  ontologyId,
  disabled = false,
}) => {
  const { authFetch } = useApiClient();
  const [isOpen, setIsOpen] = useState(false);
  const [downloading, setDownloading] = useState<'html' | 'pdf' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDownload = async (format: 'html' | 'pdf') => {
    setDownloading(format);
    setError(null);
    setIsOpen(false);

    try {
      const response = await authFetch(
        `/api/ontology-improvement/${ontologyId}/report?format=${format}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Download failed' }));
        throw new Error(errorData.error || errorData.details || `Failed to generate ${format.toUpperCase()} report`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `Ontology_Analysis_Report.${format}`;

      // Download the file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`[ReportExportButton] ${format} download error:`, err);
      setError(err instanceof Error ? err.message : `Failed to download ${format} report`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="report-export-button" ref={dropdownRef}>
      <button
        className="report-export-trigger"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || downloading !== null}
        title="Export analysis report"
      >
        {downloading ? (
          <>
            <span className="report-export-spinner" />
            Generating {downloading.toUpperCase()}...
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1v9M4.5 6.5L8 10l3.5-3.5M2 12v2h12v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Export Report
            <svg className="report-export-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </>
        )}
      </button>

      {isOpen && (
        <div className="report-export-dropdown">
          <button
            className="report-export-option"
            onClick={() => handleDownload('html')}
            disabled={downloading !== null}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 2h12v12H2z" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M4 5h8M4 8h6M4 11h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <div className="report-export-option-text">
              <span className="report-export-option-title">HTML Report</span>
              <span className="report-export-option-desc">Self-contained, viewable in browser</span>
            </div>
          </button>
          <button
            className="report-export-option"
            onClick={() => handleDownload('pdf')}
            disabled={downloading !== null}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 1h7l3 3v11H3V1z" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M10 1v3h3" stroke="currentColor" strokeWidth="1.2"/>
              <text x="5" y="11" fontSize="5" fill="currentColor" fontWeight="bold">PDF</text>
            </svg>
            <div className="report-export-option-text">
              <span className="report-export-option-title">PDF Report</span>
              <span className="report-export-option-desc">Print-ready document format</span>
            </div>
          </button>
        </div>
      )}

      {error && (
        <div className="report-export-error">
          {error}
          <button className="report-export-error-dismiss" onClick={() => setError(null)}>
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default ReportExportButton;
